import { useMutation } from "@apollo/client";
import { useCallback, useMemo } from "react";
import {
  TRACK_POST_NEW_JOB_BUTTON,
  TRACK_IMPORT_JOB_BUTTON,
  TRACK_COPY_JOB_URL,
  TRACK_OPEN_DEMO,
  TRACK_VIEW_CANDIDATES,
  TRACK_OPEN_CANDIDATE_PAGE,
  TRACK_LANDING_VISITED,
  TRACK_LANDING_APPLY_NOW,
  TRACK_INTERVIEW_INVITE_ACCEPTED,
} from "../gql/tracking.mutations";

/**
 * Custom hook for tracking various user events.
 * Provides methods for tracking company and candidate actions.
 */
const useTracking = () => {
  const [trackPostNewJobButtonMutation] = useMutation(TRACK_POST_NEW_JOB_BUTTON);
  const [trackImportJobButtonMutation] = useMutation(TRACK_IMPORT_JOB_BUTTON);
  const [trackCopyJobUrlMutation] = useMutation(TRACK_COPY_JOB_URL);
  const [trackOpenDemoMutation] = useMutation(TRACK_OPEN_DEMO);
  const [trackViewCandidatesMutation] = useMutation(TRACK_VIEW_CANDIDATES);
  const [trackOpenCandidatePageMutation] = useMutation(TRACK_OPEN_CANDIDATE_PAGE);
  const [trackLandingVisitedMutation] = useMutation(TRACK_LANDING_VISITED);
  const [trackLandingApplyNowMutation] = useMutation(TRACK_LANDING_APPLY_NOW);
  const [trackInterviewInviteAcceptedMutation] = useMutation(TRACK_INTERVIEW_INVITE_ACCEPTED);

  /**
   * Track when company clicks "Post New Job" button
   * @param {string} [clientId] - The client/company ID
   */
  const trackPostNewJobButton = useCallback(
    async (clientId) => {
      try {
        await trackPostNewJobButtonMutation({ variables: { clientId } });
      } catch (error) {
        console.error("Failed to track post new job button:", error);
      }
    },
    [trackPostNewJobButtonMutation]
  );

  /**
   * Track when company clicks "Import a job" button
   * @param {string} [clientId] - The client/company ID
   */
  const trackImportJobButton = useCallback(
    async (clientId) => {
      try {
        await trackImportJobButtonMutation({ variables: { clientId } });
      } catch (error) {
        console.error("Failed to track import job button:", error);
      }
    },
    [trackImportJobButtonMutation]
  );

  /**
   * Track when company copies a job URL
   * @param {string} clientId - The client/company ID (required)
   * @param {string} jobId - The job ID (required)
   * @param {string} [jobTitle] - The job title
   */
  const trackCopyJobUrl = useCallback(
    async (clientId, jobId, jobTitle) => {
      try {
        await trackCopyJobUrlMutation({ variables: { clientId, jobId, jobTitle } });
      } catch (error) {
        console.error("Failed to track copy job URL:", error);
      }
    },
    [trackCopyJobUrlMutation]
  );

  /**
   * Track when company opens demo mode
   * @param {string} [clientId] - The client/company ID
   * @param {string} [jobId] - The job ID
   */
  const trackOpenDemo = useCallback(
    async (clientId, jobId) => {
      try {
        await trackOpenDemoMutation({ variables: { clientId, jobId } });
      } catch (error) {
        console.error("Failed to track open demo:", error);
      }
    },
    [trackOpenDemoMutation]
  );

  /**
   * Track when company views candidates list
   * @param {string} clientId - The client/company ID (required)
   * @param {string} jobId - The job ID (required)
   * @param {string} [jobTitle] - The job title
   * @param {number} [candidatesCount] - The number of candidates
   */
  const trackViewCandidates = useCallback(
    async (clientId, jobId, jobTitle, candidatesCount) => {
      try {
        await trackViewCandidatesMutation({
          variables: { clientId, jobId, jobTitle, candidatesCount },
        });
      } catch (error) {
        console.error("Failed to track view candidates:", error);
      }
    },
    [trackViewCandidatesMutation]
  );

  /**
   * Track when company opens a specific candidate's page
   * @param {string} clientId - The client/company ID (required)
   * @param {string} jobId - The job ID (required)
   * @param {string} applicationId - The application ID (required)
   * @param {string} [candidateId] - The candidate/user ID
   */
  const trackOpenCandidatePage = useCallback(
    async (clientId, jobId, applicationId, candidateId) => {
      try {
        await trackOpenCandidatePageMutation({
          variables: { clientId, jobId, applicationId, candidateId },
        });
      } catch (error) {
        console.error("Failed to track open candidate page:", error);
      }
    },
    [trackOpenCandidatePageMutation]
  );

  /**
   * Track when candidate visits landing page (no auth required)
   * @param {string} [clientId] - The client/company ID
   * @param {string} [jobId] - The job ID
   * @param {string} [referrer] - The referrer URL
   * @param {string} [utmSource] - UTM source parameter
   * @param {string} [utmMedium] - UTM medium parameter
   * @param {string} [utmCampaign] - UTM campaign parameter
   */
  const trackLandingVisited = useCallback(
    async (clientId, jobId, referrer, utmSource, utmMedium, utmCampaign) => {
      try {
        await trackLandingVisitedMutation({
          variables: { clientId, jobId, referrer, utmSource, utmMedium, utmCampaign },
        });
      } catch (error) {
        console.error("Failed to track landing visited:", error);
      }
    },
    [trackLandingVisitedMutation]
  );

  /**
   * Track when candidate clicks "Apply Now" on landing page (no auth required)
   * @param {string} clientId - The client/company ID (required)
   * @param {string} jobId - The job ID (required)
   * @param {string} [jobTitle] - The job title
   */
  const trackLandingApplyNow = useCallback(
    async (clientId, jobId, jobTitle) => {
      try {
        await trackLandingApplyNowMutation({
          variables: { clientId, jobId, jobTitle },
        });
      } catch (error) {
        console.error("Failed to track landing apply now:", error);
      }
    },
    [trackLandingApplyNowMutation]
  );

  /**
   * Track when candidate accepts interview invite
   * @param {string} applicationId - The application ID (required)
   * @param {string} jobId - The job ID (required)
   * @param {string} clientId - The client/company ID (required)
   */
  const trackInterviewInviteAccepted = useCallback(
    async (applicationId, jobId, clientId) => {
      try {
        await trackInterviewInviteAcceptedMutation({
          variables: { applicationId, jobId, clientId },
        });
      } catch (error) {
        console.error("Failed to track interview invite accepted:", error);
      }
    },
    [trackInterviewInviteAcceptedMutation]
  );

  return useMemo(
    () => ({
      trackPostNewJobButton,
      trackImportJobButton,
      trackCopyJobUrl,
      trackOpenDemo,
      trackViewCandidates,
      trackOpenCandidatePage,
      trackLandingVisited,
      trackLandingApplyNow,
      trackInterviewInviteAccepted,
    }),
    [
      trackPostNewJobButton,
      trackImportJobButton,
      trackCopyJobUrl,
      trackOpenDemo,
      trackViewCandidates,
      trackOpenCandidatePage,
      trackLandingVisited,
      trackLandingApplyNow,
      trackInterviewInviteAccepted,
    ]
  );
};

export default useTracking;
